import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Brain, ArrowUpRight, TrendingUp } from 'lucide-react';
export function SEOvsAISEOSlide() {
  const [traffic, setTraffic] = useState(5000);
  // More conservative, research-backed model:
  // Local service business conversion rates (Unbounce benchmark report):
  // - Organic visitor → lead: 2-4% (form fill, phone call)
  // - Lead → customer: 15-25% close rate
  // - Average customer value varies, use $500 as baseline (conservative for services)
  //
  // Old SEO: 2.5% visitor-to-lead, 15% close = 0.375% visitor-to-customer
  // AI-SEO: 3.5% visitor-to-lead (better intent), 20% close (AI chatbot pre-qualifies) = 0.7%
  //
  // Revenue = traffic × 12 months × conversion × avg customer value ($500)
  const avgCustomerValue = 500; // Conservative baseline
  // Old SEO: 0.375% of visitors become customers
  const oldConversion = 0.00375;
  const oldCustomersPerYear = traffic * 12 * oldConversion;
  const oldRevenue = oldCustomersPerYear * avgCustomerValue;
  // AI-SEO: 0.7% of visitors become customers (better intent matching + AI chatbot)
  const aiConversion = 0.007;
  const aiCustomersPerYear = traffic * 12 * aiConversion;
  const aiRevenue = aiCustomersPerYear * avgCustomerValue;
  const additionalRevenue = aiRevenue - oldRevenue;
  const formatCurrency = (val: number) => {
    if (val >= 1000000) return '$' + (val / 1000000).toFixed(1) + 'M';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  return <section id="ai-seo" className="min-h-screen bg-dark-section py-24 px-4 flex flex-col justify-center" data-id="element-453" data-mp="1yskkyoxlh08b">
      <div className="max-w-7xl mx-auto w-full" data-id="element-454" data-mp="22t4ezjmvfnq8">
        <div className="text-center mb-16" data-id="element-455" data-mp="22rtb7jtfefz4">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-456" data-mp="1otv32w5p7fs2">
            The New Game in 2026
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-457" data-mp="3mpk0dugs5x9n">
            Traditional SEO vs AI-SEO
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16" data-id="element-458" data-mp="9a4717aj97kb">
          {/* Old SEO */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-dark-card border border-white/5 rounded-2xl p-8 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-500" data-id="element-459" data-mp="4cvpn8x7l7k4">
            <div className="flex items-center mb-6" data-id="element-460" data-mp="2jxqxkpt8a6c0">
              <div className="p-3 bg-white/10 rounded-lg mr-4" data-id="element-461" data-mp="2ow0ylh1mw15j">
                <Search size={32} data-id="element-462" data-mp="2ouppr2tc037k" />
              </div>
              <h3 className="text-2xl font-bold" data-id="element-463" data-mp="2to2fxhdnsi2f">Old SEO (2008 - 2025)</h3>
            </div>
            <ul className="space-y-4 text-text-muted mb-8" data-id="element-464" data-mp="2xnugp7wt9jx8">
              <li className="flex items-start" data-id="element-465" data-mp="32nywswtt4cmx">
                <span className="mr-2" data-id="element-466" data-mp="32mnrutomkyu8">•</span> Keyword stuffing & backlink
                farming
              </li>
              <li className="flex items-start" data-id="element-467" data-mp="37ntmwy5rcy6x">
                <span className="mr-2" data-id="element-468" data-mp="3cmofusxfw34r">•</span> Months to see results
              </li>
              <li className="flex items-start" data-id="element-469" data-mp="1d9akswp4owpf">
                <span className="mr-2" data-id="element-470" data-mp="1dalo6venw2g0">•</span> Constant algorithm updates
              </li>
            </ul>
            <div className="p-4 bg-white/5 rounded-xl border border-white/10" data-id="element-471" data-mp="1i8dskowhth13">
              <span className="text-sm uppercase tracking-wider text-text-dim font-bold" data-id="element-472" data-mp="38nwheruxv6h8">
                Focus
              </span>
              <p className="text-lg font-medium mt-1" data-id="element-473" data-mp="33s8s8cggidbq">
                Ranking #1 for specific keywords
              </p>
            </div>
          </motion.div>

          {/* AI SEO */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-dark-card border border-primary/30 rounded-2xl p-8 relative overflow-hidden shadow-2xl shadow-primary/10" data-id="element-474" data-mp="3crm2v8gbh7y1">
            <div className="flex items-center mb-6" data-id="element-475" data-mp="2ty8g3qmrc7ti">
              <div className="p-3 bg-primary/20 rounded-lg mr-4" data-id="element-476" data-mp="269yy6zso047n">
                <Brain className="text-primary" size={32} data-id="element-477" data-mp="21bnqovpue5sl" />
              </div>
              <h3 className="text-2xl font-bold text-white" data-id="element-478" data-mp="219l30ic5wor1">
                New AI-SEO (2026+)
              </h3>
            </div>
            <ul className="space-y-4 text-text-muted mb-8" data-id="element-479" data-mp="lk3e2joieon6">
              <li className="flex items-start" data-id="element-480" data-mp="llmvweptd7r2">
                <span className="mr-2 text-primary" data-id="element-481" data-mp="7tglzad97x02">✓</span> Content-first, AI
                chatbot integration
              </li>
              <li className="flex items-start" data-id="element-482" data-mp="csjiu7j2ywwy">
                <span className="mr-2 text-primary" data-id="element-483" data-mp="10hvpazn0wqvk">✓</span> Conversational
                search optimization
              </li>
              <li className="flex items-start" data-id="element-484" data-mp="14i66l8g9n3pq">
                <span className="mr-2 text-primary" data-id="element-485" data-mp="14gv3zbyk4207">✓</span> Intent-based
                ranking
              </li>
            </ul>
            <div className="p-4 bg-primary/10 rounded-xl border border-primary/20" data-id="element-486" data-mp="vhzw7vfv6jus">
              <span className="text-sm uppercase tracking-wider text-primary font-bold" data-id="element-487" data-mp="3b9njt08zn00x">
                Focus
              </span>
              <p className="text-lg font-medium mt-1 text-white" data-id="element-488" data-mp="3g9rxyjmfbinz">
                Capturing AI-generated traffic
              </p>
            </div>
          </motion.div>
        </div>

        {/* Data Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-2xl p-8 border border-white/10 mb-16 text-center relative overflow-hidden" data-id="element-489" data-mp="2s6bumlx63spl">
          <div className="relative z-10" data-id="element-490" data-mp="28g9samlsykdc">
            <p className="text-lg md:text-xl font-medium leading-relaxed" data-id="element-491" data-mp="24fg8a4bhdppq">
              <span className="text-primary font-bold" data-id="element-492" data-mp="24hk9gehzwamb">Airbnb Data:</span> 14%
              of their 240M annual visitors use ChatGPT for travel planning.
              <br className="hidden md:block" data-id="element-493" data-mp="2ocwvctro744" />
              That's{' '}
              <span className="text-white font-bold" data-id="element-494" data-mp="3tunq4msjc3kw">
                33.6 million potential customers
              </span>{' '}
              who've never hit your Google ranking.
            </p>
          </div>
        </motion.div>

        {/* Revenue Impact Calculator */}
        <div className="bg-dark-card rounded-2xl p-8 border border-white/10" data-id="element-495" data-mp="a5j4xpvo6q95">
          <h3 className="text-2xl font-bold mb-2 text-center" data-id="element-496" data-mp="eyuevzvs3n5a">
            Projected Revenue Impact
          </h3>
          <p className="text-text-muted text-center text-sm mb-8" data-id="element-497" data-mp="jzi3yg0g5twv">
            Based on Unbounce conversion benchmarks • $500 avg customer value
          </p>

          <div className="mb-12" data-id="element-498" data-mp="3nf73l3pyk8n6">
            <label className="block text-sm font-medium text-text-muted mb-4 text-center" data-id="element-499" data-mp="3nee2ai9rqbcf">
              Monthly Website Traffic:{' '}
              <span className="text-white font-bold text-lg" data-id="element-500" data-mp="96fx5b2l0kq">
                {traffic.toLocaleString()}
              </span>
            </label>
            <input type="range" min="500" max="50000" step="500" value={traffic} onChange={e => setTraffic(parseInt(e.target.value))} className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-primary hover:accent-primary-hover" data-id="element-501" data-mp="17mghb4t9slqk" />
            <div className="flex justify-between text-xs text-text-dim mt-2" data-id="element-502" data-mp="27g6p6osoixpu">
              <span data-id="element-503" data-mp="22phyhbotq4rr">500</span>
              <span data-id="element-504" data-mp="22o6vhe37d11p">50,000</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-505" data-mp="2ggmtg0h8y8vr">
            <div className="p-6 rounded-xl bg-white/5 border border-white/5" data-id="element-506" data-mp="2ges66fii64oi">
              <h4 className="text-text-muted font-medium mb-4" data-id="element-507" data-mp="2bgqmbsd961bz">
                Old SEO Approach
              </h4>
              <div className="space-y-4" data-id="element-508" data-mp="2qe16dq3yofjb">
                <div className="flex justify-between" data-id="element-509" data-mp="2lfzdyeknb3v6">
                  <span className="text-sm" data-id="element-510" data-mp="2lghfnqq2uaah">Visitor → Customer</span>
                  <span className="font-mono" data-id="element-511" data-mp="w1t3nqleeucz">0.38%</span>
                </div>
                <div className="flex justify-between" data-id="element-512" data-mp="r284bf2bgjzq">
                  <span className="text-sm" data-id="element-513" data-mp="r3rnbdfcx957">Customers / Year</span>
                  <span className="font-mono text-white" data-id="element-514" data-mp="32q3cbjxs8g9d">
                    {Math.round(oldCustomersPerYear)}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-515" data-mp="2sz8owc8svamc">
                  <span className="text-sm" data-id="element-516" data-mp="2sypdzfhkhmht">Est. Revenue</span>
                  <span className="font-mono text-white" data-id="element-517" data-mp="2xxsc0fz4qsga">
                    {formatCurrency(oldRevenue)}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-primary/10 border border-primary/30 relative overflow-hidden" data-id="element-518" data-mp="2o0nke0mim4s3">
              <div className="absolute top-0 right-0 p-2" data-id="element-519" data-mp="2a162m57mzrs8">
                <TrendingUp className="text-primary opacity-20" size={64} data-id="element-520" data-mp="2a2pj9wx7g4uj" />
              </div>
              <h4 className="text-primary font-bold mb-4 flex items-center" data-id="element-521" data-mp="2f1k3n3atm1gr">
                AI-SEO (LetLois) <ArrowUpRight size={16} className="ml-2" data-id="element-522" data-mp="86wzrrttmv7q" />
              </h4>
              <div className="space-y-4 relative z-10" data-id="element-523" data-mp="d7kr6elype2h">
                <div className="flex justify-between" data-id="element-524" data-mp="3b0src8q5derg">
                  <span className="text-sm" data-id="element-525" data-mp="3b017fv5wx5h0">Visitor → Customer</span>
                  <span className="font-mono text-white" data-id="element-526" data-mp="3ft2mq4a17714">
                    0.70% <span className="text-green-400 text-xs" data-id="element-527" data-mp="320w84mmj183s">(+87%)</span>
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-528" data-mp="36yxsrbzlpfhd">
                  <span className="text-sm" data-id="element-529" data-mp="36xcwnhxn1ohe">Customers / Year</span>
                  <span className="font-mono text-white" data-id="element-530" data-mp="3uqmmmws2mc0f">
                    {Math.round(aiCustomersPerYear)}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-531" data-mp="3krdwyg48njjv">
                  <span className="text-sm" data-id="element-532" data-mp="3kqvuh1qzg93i">Est. Revenue</span>
                  <span className="font-mono text-white text-lg font-bold" data-id="element-533" data-mp="3ppfh74dehowx">
                    {formatCurrency(aiRevenue)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <motion.div key={traffic} initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="mt-8 p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-center" data-id="element-534" data-mp="2ecmsk2fwpgl3">
            <p className="text-green-400 font-bold text-lg md:text-xl" data-id="element-535" data-mp="2at4gywvcgya2">
              Additional Revenue: {formatCurrency(additionalRevenue)} / year
            </p>
            <p className="text-text-dim text-xs mt-1" data-id="element-536" data-mp="1n4314ok6mkur">
              That's {Math.round(aiCustomersPerYear - oldCustomersPerYear)} more
              customers per year from the same traffic
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}